import { Link } from "react-router-dom";
import { MapPin, Settings, RefreshCw, AlertTriangle, Compass, ExternalLink } from "lucide-react";

/**
 * Full-screen recovery UI when geolocation fails.
 * Handles four distinct failure modes:
 *   - "iframe_blocked": app is embedded in a Preview/wrapper iframe whose
 *                      Permissions-Policy blocks geolocation. Device setting
 *                      is irrelevant — user must open the app directly.
 *   - "denied":    user (or browser policy) blocked location access
 *   - "unavailable": device can't get a fix (rare, usually hardware or airplane mode)
 *   - "timeout":   GPS didn't lock in time (common indoors, on cold start, iOS Safari)
 *
 * Renders fixed / inset-0 / z-50 so it fully covers whatever's underneath.
 * Full-height scroll is contained inside this component only.
 */
export default function LocationDenied({ onRetry, reason = "denied" }) {
  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  const isIOS = /iPhone|iPad|iPod/i.test(ua);
  const isAndroid = /Android/i.test(ua);
  const isSafari = /Safari/i.test(ua) && !/CriOS|FxiOS|Chrome/i.test(ua);
  const isInApp = /Instagram|FBAN|FBAV|Twitter|Line|Snapchat|TikTok|LinkedInApp|Discord|Slack|Emergent/i.test(ua)
    // Generic iOS webview heuristic: on iPhone but not real Safari and not Chrome/Firefox iOS builds
    || (isIOS && !isSafari && !/CriOS|FxiOS|EdgiOS/i.test(ua));

  // The direct app URL — same for the framed case (iframe src === child URL).
  // target="_blank" breaks out of the wrapper into a real Safari tab.
  const directUrl = typeof window !== "undefined" ? window.location.href : "";

  const copy = {
    iframe_blocked: {
      icon: <ExternalLink size={36} className="text-white" />,
      title: "Open in Safari.",
      overline: "THIS PREVIEW WRAPPER BLOCKS GPS — YOUR PHONE'S LOCATION IS FINE",
    },
    denied: {
      icon: <MapPin size={36} className="text-white" />,
      title: "Location is off.",
      overline: "BLINKLINK ONLY WORKS WHEN YOU'RE ACTUALLY THERE",
    },
    unavailable: {
      icon: <AlertTriangle size={36} className="text-white" />,
      title: "Can't reach GPS.",
      overline: "YOUR DEVICE ISN'T RETURNING A POSITION",
    },
    timeout: {
      icon: <Compass size={36} className="text-white" />,
      title: "GPS took too long.",
      overline: "TRY AGAIN — YOUR PHONE MAY BE INDOORS OR JUST WOKE UP",
    },
  }[reason] || {
    icon: <MapPin size={36} className="text-white" />,
    title: "Location unavailable.",
    overline: "SOMETHING BLOCKED YOUR LOCATION",
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black text-white overflow-y-auto overscroll-contain"
      data-testid="location-denied"
      data-reason={reason}
    >
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(230,0,0,0.15)_0%,transparent_70%)] pointer-events-none" />
      <div className="relative max-w-md mx-auto w-full px-6 pt-14 pb-10 min-h-full flex flex-col">
        <div className="mx-auto w-20 h-20 rounded-full bg-gradient-to-br from-[#E60000] to-[#660000] flex items-center justify-center shadow-[0_0_40px_rgba(230,0,0,0.5)]">
          {copy.icon}
        </div>
        <h1
          className="mt-8 text-3xl font-bold text-center"
          style={{ fontFamily: "'Manrope',sans-serif", fontWeight: 800 }}
          data-testid="denied-title"
        >
          {copy.title}
        </h1>
        <p className="mt-3 font-mono-accent text-zinc-400 text-center tracking-[0.25em] text-[0.7rem] px-4 leading-relaxed">
          {copy.overline}
        </p>

        {/* iframe_blocked: dedicated escape-hatch UI */}
        {reason === "iframe_blocked" && (
          <>
            <div className="mt-8 rounded-2xl border border-white/10 bg-black/50 p-5">
              <p className="text-zinc-300 text-sm leading-relaxed">
                You're viewing BlinkLink inside a <span className="text-white font-semibold">Preview wrapper</span> that
                blocks the location API — no matter what your phone's Location Services says.
                {" "}<span className="text-white font-semibold">Open the app directly in Safari</span> and everything works.
              </p>
            </div>
            <div className="mt-4 rounded-2xl border border-white/10 bg-black/50 p-5">
              <div className="flex items-center gap-2 mb-3">
                <Settings size={14} className="text-[#E60000]" />
                <p className="font-mono-accent text-[#E60000] tracking-[0.2em] text-[0.7rem]">HOW TO FIX</p>
              </div>
              <ol className="text-zinc-300 text-sm space-y-2 leading-relaxed" data-testid="instr-iframe">
                <li><span className="text-[#E60000] font-bold">1.</span> Tap the <span className="font-semibold">OPEN IN SAFARI</span> button below</li>
                <li><span className="text-[#E60000] font-bold">2.</span> A new Safari tab opens with the real app URL</li>
                <li><span className="text-[#E60000] font-bold">3.</span> Grant location when Safari asks — then <span className="font-semibold">Scan Nearby</span> works</li>
              </ol>
            </div>
            <a
              href={directUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="denied-open-safari"
              className="mt-8 w-full py-4 rounded-full bg-gradient-to-r from-[#E60000] to-[#660000] hover:brightness-110 font-bold tracking-wider transition active:scale-[0.98] shadow-[0_0_30px_rgba(230,0,0,0.4)] flex items-center justify-center gap-2"
            >
              <ExternalLink size={16}/> OPEN IN SAFARI
            </a>
            <button
              onClick={onRetry}
              data-testid="denied-retry-iframe"
              className="mt-4 w-full py-3 rounded-full border border-white/15 hover:border-white/30 font-mono-accent tracking-[0.2em] text-xs flex items-center justify-center gap-2"
            >
              <RefreshCw size={14}/> ALREADY IN SAFARI? TRY AGAIN
            </button>
          </>
        )}

        {/* Timeout / unavailable: emphasize retry */}
        {(reason === "timeout" || reason === "unavailable") && (
          <div className="mt-8 rounded-2xl border border-white/10 bg-black/50 p-5">
            <p className="text-zinc-300 text-sm leading-relaxed">
              {reason === "timeout" ? (
                <>
                  Your phone's GPS didn't respond in the time we gave it.
                  This is common indoors, right after unlocking your phone, or on the first launch of the day.
                  Wait 5–10 seconds and tap <span className="text-white font-semibold">Try Again</span>.
                </>
              ) : (
                <>
                  Your device didn't return a position. Check that <span className="text-white font-semibold">Location Services</span> is on, and that <span className="text-white font-semibold">Airplane Mode</span> is off.
                </>
              )}
            </p>
          </div>
        )}

        {/* Denied: full recovery instructions */}
        {reason === "denied" && (
          <>
            <div className="mt-8 rounded-2xl border border-white/10 bg-black/50 p-5">
              <p className="text-zinc-300 text-sm leading-relaxed">
                BlinkLink resolves your location to a <span className="text-white font-semibold">verified venue</span> or an
                {" "}<span className="text-white font-semibold">approximate 150-meter radius</span>. Coordinates are stored
                ephemerally on your session only — never on your profile, never sold, never shared with other users.
              </p>
            </div>

            {isInApp && (
              <div className="mt-4 rounded-2xl border border-[#FF1A1A]/40 bg-[#E60000]/10 p-4" data-testid="warn-in-app">
                <p className="text-[#FFB4B4] text-sm leading-relaxed">
                  <span className="font-semibold">You're in an in-app browser</span> (Instagram, TikTok, Twitter, Snapchat, etc.).
                  These often block location. <span className="font-semibold">Tap the "•••" menu → "Open in Safari"</span> and try again there.
                </p>
              </div>
            )}

            <div className="mt-4 rounded-2xl border border-white/10 bg-black/50 p-5">
              <div className="flex items-center gap-2 mb-3">
                <Settings size={14} className="text-[#E60000]" />
                <p className="font-mono-accent text-[#E60000] tracking-[0.2em] text-[0.7rem]">HOW TO ENABLE</p>
              </div>
              {isIOS && (
                <ol className="text-zinc-300 text-sm space-y-2 leading-relaxed" data-testid="instr-ios">
                  <li><span className="text-[#E60000] font-bold">1.</span> Open <span className="font-semibold">iPhone Settings → Privacy &amp; Security → Location Services</span> and make sure it's <span className="font-semibold">ON</span></li>
                  <li><span className="text-[#E60000] font-bold">2.</span> Scroll to <span className="font-semibold">{isSafari ? "Safari Websites" : "your browser"}</span>, tap it, choose <span className="font-semibold">While Using the App</span></li>
                  <li><span className="text-[#E60000] font-bold">3.</span> Toggle <span className="font-semibold">Precise Location</span> to <span className="font-semibold">ON</span> (BlinkLink cannot work with approximate mode)</li>
                  <li><span className="text-[#E60000] font-bold">4.</span> Return to BlinkLink and tap <span className="font-semibold">Try Again</span></li>
                </ol>
              )}
              {isAndroid && (
                <ol className="text-zinc-300 text-sm space-y-2 leading-relaxed" data-testid="instr-android">
                  <li><span className="text-[#E60000] font-bold">1.</span> Tap the <span className="font-semibold">lock icon</span> in the address bar</li>
                  <li><span className="text-[#E60000] font-bold">2.</span> Choose <span className="font-semibold">Permissions → Location → Allow</span></li>
                  <li><span className="text-[#E60000] font-bold">3.</span> Also check <span className="font-semibold">Android Settings → Location</span> is ON</li>
                  <li><span className="text-[#E60000] font-bold">4.</span> Reload and tap <span className="font-semibold">Try Again</span></li>
                </ol>
              )}
              {!isIOS && !isAndroid && (
                <ol className="text-zinc-300 text-sm space-y-2 leading-relaxed" data-testid="instr-desktop">
                  <li><span className="text-[#E60000] font-bold">1.</span> Click the <span className="font-semibold">lock / info icon</span> next to the URL</li>
                  <li><span className="text-[#E60000] font-bold">2.</span> Set <span className="font-semibold">Location</span> to <span className="font-semibold">Allow</span></li>
                  <li><span className="text-[#E60000] font-bold">3.</span> Reload the page, then tap <span className="font-semibold">Try Again</span></li>
                </ol>
              )}
            </div>
          </>
        )}

        {reason !== "iframe_blocked" && (
          <>
            <button
              onClick={onRetry}
              data-testid="denied-retry"
              className="mt-8 w-full py-4 rounded-full bg-gradient-to-r from-[#E60000] to-[#660000] hover:brightness-110 font-bold tracking-wider transition active:scale-[0.98] shadow-[0_0_30px_rgba(230,0,0,0.4)] flex items-center justify-center gap-2"
            >
              <RefreshCw size={16}/> TRY AGAIN
            </button>
            <Link to="/home" className="mt-4 text-center font-mono-accent text-zinc-500 hover:text-white tracking-[0.3em] text-xs" data-testid="denied-back-home">
              BACK TO HOME
            </Link>
          </>
        )}
        {reason === "iframe_blocked" && (
          <Link to="/home" className="mt-6 text-center font-mono-accent text-zinc-500 hover:text-white tracking-[0.3em] text-xs" data-testid="denied-back-home">
            BACK TO HOME
          </Link>
        )}

        <p className="mt-auto pt-8 text-center font-mono-accent text-zinc-700 text-[0.6rem] tracking-widest">
          COORDS STORED EPHEMERALLY · 90 MIN TTL · NEVER ON YOUR PROFILE
        </p>
      </div>
    </div>
  );
}
